import { Configurator } from './configurator';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { User, Payment, ThreeDsTypes, HotelOffers, Currency } from './index';

export namespace Order {
  export namespace Ml {
    export namespace Request {
      export interface Body {
        offerToken: string;
        useCredits: boolean;
        booker: Booker;
        guests: Guest[];
        remarks: Remarks;
        cardDetails?: CardDetails;
        cardInfo?: CardInfo;
        metadata?: Metadata;
      }

      export interface Booker {
        firstName: string;
        lastName: string;
        phone: string;
        email: string;
        billingAddress: BillingAddress;
      }

      export interface BillingAddress {
        address: string;
        city: string;
        country: string;
        postCode: string;
      }

      export interface Guest {
        name: string;
        email: string;
      }

      export interface Remarks {
        arrivalTime?: number;
        arrivalNextDay?: boolean;
        specialRequest?: string;
      }

      export interface CardDetails {
        number: string;
        cardHolder: string;
        year: string;
        month: string;
        cvc: string;
      }

      export interface CardInfo {
        cardId: string;
        cvc: string;
      }

      export interface Metadata {
        expedia?: ExpediaMetadata;
        processedByServer?: boolean;
      }

      export interface ExpediaMetadata {
        version?: string;
        encodedBrowserMetadata?: string;
      }
    }

    export namespace Response {
      export interface Booking {
        order: Order;
        metadata: Metadata;
      }

      export interface Order {
        _id: number;
        id?: string;
        externalId?: string;
        status?: string;
        type?: string;
        provider?: string;
        created?: string;
        checkin?: string;
        checkout?: string;
        complete?: string;
        userName?: string;
        travelers?: string[];
        hotelId?: number;
        hotelLabel?: string;
        hotelName?: string;
        hotelAddress?: string;
        room?: string;
        description?: string;
        comment?: string;
        amount?: number;
        currency?: string;
        cashback?: number;
        save?: number;
        orderUrl?: string;
      }

      export interface Metadata {
        expedia?: ExpediaMetadata;
        booking?: BookingMetadata;
        itravex?: ItravexMetadata;
        serverBookingFlow?: boolean;
      }

      export interface ExpediaMetadata {
        paymentSessionId?: string;
        encodedInitConfig?: string;
        encodedChallengeConfig?: string;
        decta3DS?: string;
      }

      export interface BookingMetadata {
        pci3DS?: string;
      }

      export interface ItravexMetadata {
        decta3DS?: string;
      }

      export interface OrderDetails {
        _id?: number;
        id?: string;
        userId?: string;
        externalId?: string;
        orderNumber?: string;
        hotelConfirmationNumber?: string | string[];
        providerConfirmationNumber?: string;
        status?: OrderStatus;
        type?: OrderTypes;
        provider?: string;
        created?: string;
        checkin?: string;
        checkout?: string;
        complete?: string;
        userName?: string;
        userEmail?: string;
        userPhone?: string;
        travelers?: string[];
        checkInTimePeriod?: TimePeriod;
        checkOutTimePeriod?: TimePeriod;
        allocation?: {
          adults?: number;
          children?: number[];
        };
        days?: number;

        hotelId?: number;
        hotelLabel?: string;
        hotelName?: string;
        hotelAddress?: string;
        hotelStars?: number;
        hotelLatitude?: number;
        hotelLongitude?: number;
        hotelPhone?: string;
        hotelEmail?: string;
        hotelCity?: string;
        hotelCountry?: string;
        roomName?: string;
        roomOriginalName?: string;
        roomDescription?: string;
        roomSize?: number;
        roomAmenities?: unknown[];
        specialRequests?: string;
        arrivalTime?: string;
        hotelAcceptedPaymentMethods?: string[];
        cardNumber?: string;
        cardType?: string;
        currency?: Currency.CURRENCIES | string;
        billingCurrency?: Currency.CURRENCIES | string;
        price?: HotelOffers.Price;
        freeBreakfast?: boolean;
        freeCancellationUntil?: string;
        cancellationPolicies?: HotelOffers.CancellationPolicies[];
        paymentSchedule?: HotelOffers.PaymentSchedule[];
        canBeRefunded?: boolean;
        refunded?: boolean;
        orderUrl?: string;
        orderType?: OrderProductType;
        policyCancellationType?: HotelOffers.PolicyCancellationType;
      }

      export interface TimePeriod {
        from?: string;
        to?: string;
      }
    }

    export enum OrderStatus {
      Absent = 'absent',
      Issued = 'issued',
      Created = 'created',
      Need2fa = 'need_2fa',
      Pending = 'pending',
      Stayed = 'stayed',
      Booked = 'booked',
      ToCancel = 'to_cancel',
      Canceled = 'canceled',
      Errored = 'errored',
      Failed = 'failed',
      NoShow = 'no_show',
      Completed = 'completed',
      Hold = 'hold',
      Paid = 'paid',
      Refunded = 'refunded',
    }

    export enum OrderTypes {
      Affiliate = 'affiliate',
      Internal = 'internal',
      Test = 'test',
    }

    export enum OrderProductType {
      Hotel = 'hotel',
      Car = 'car',
    }
  }

  export class Body {
    new_plan_id: string;
    product: string;
    payment_info: PaymentInfo;
    contact_info: ContactInfo;
    payload: Payload;
    travellers: Traveller[];
    // subscription_purchase_option: string;
    savings_cents: number;
    version: number; // current 2
    hotelId: string;
    threeDsMeta?: ThreeDsTypes.Meta;

    constructor(
      cvv: string,
      guest: User.Guest,
      contacts: User.Contacts,
      card: User.Card,
      price: Price,
      planId: string,
      productId: number | string,
      specialRequest: string,
      arrivalTime: string,
      product: OrderProduct,
      threeDsMeta?: ThreeDsTypes.Meta,
      loyaltyProgramNumber?: string,
    ) {
      this.product = product;
      this.payment_info = new PaymentInfo(card, cvv);
      this.contact_info = new ContactInfo(contacts);
      let payload = null;
      switch (product) {
        case OrderProduct.car: {
          payload = new CarPayload(String(productId));
          break;
        }
        case OrderProduct.hotel: {
          payload = new HotelPayload(price, specialRequest, arrivalTime);
          break;
        }
      }
      this.payload = payload;
      this.travellers = [new Traveller(guest, loyaltyProgramNumber)];
      // this.subscription_purchase_option = split ? split : 'free';
      this.savings_cents = price.savings;
      this.new_plan_id = planId;
      this.version = 2;
      this.hotelId = String(productId);
      this.threeDsMeta = threeDsMeta;
    }

    toObject(): object {
      const body = {
        product: this.product,
        payment_info: this.hasNewCard()
          ? this.payment_info.getPaymentInfoWithNewCard()
          : this.payment_info.getPaymentInfoWithSavedCard(),
        contact_info: this.contact_info,
        payload: this.payload,
        travellers: this.travellers,
        savings_cents: this.savings_cents,
        version: this.version,
      };

      if (this.new_plan_id) {
        body['new_plan_id'] = this.new_plan_id;
      }

      if (this.threeDsMeta) {
        body.payload['encoded_browser_metadata'] =
          this.threeDsMeta.encodedBrowserMetadata;
        body.payload['merchant_url'] = 'https://hotel.finalprice.com';
        body.payload['preferred_challenge_window_size'] = 'large';
      }

      const configurator = Configurator.builder();

      if (configurator) {
        body.payload['source_settings'] = configurator.eps.toRequestBody();
      }
      return body;
    }

    hasNewCard(): boolean {
      return this.payment_info.card.temporary !== undefined;
    }
  }

  export interface OrderResponse {
    status?: string;
    supplier?: string;
    order_id?: string;
    order_number?: string;
    order_status?: string;
    message?: string;
    card_id?: string;
    amount?: number;
    client_token?: string;
    card?: Payment.CardCredentials;
    error?: string;
    event_id?: string[];
    detail?: any[];
    is_3ds_required?: boolean;
    encoded_challenge_config?: string;
    encoded_init_config?: string;
    payment_session_id?: string;
    payment_status?: PaymentStatus;
    thrred_url?: string;
  }

  export interface DeferredOrderPayment {
    orderId: string;
    thrredUrl: string;
    source: PendingOrderSource;
  }

  export interface DeferredOrderCancellationState {
    loading?: boolean;
    show?: boolean;
    error: string;
  }

  export interface UsersPendingOrders {
    id: string;
    thrred_url?: string;
  }

  export interface OrderDetails {
    id: string;
    product: Order.OrderProduct;
  }

  export enum PaymentStatus {
    Canceled = 'canceled',
    InProgress = 'in_progress',
    Paid = 'paid',
    Hold = 'hold',
  }

  export class PaymentInfo {
    card_id: string;
    cvv: string;
    card: User.Card;
    nonce: string;

    constructor(card: User.Card, cvv: string) {
      this.card_id = card.card_id ? card.card_id : '';
      this.cvv = cvv;
      this.card = card;
      this.nonce = '';
    }

    getPaymentInfoWithNewCard() {
      const paymentInfo = {
        card: this.card.temporary.toRequestModel(this.cvv),
      };
      if (this.nonce) paymentInfo['three_ds'] = this.nonce;
      return paymentInfo;
    }

    getPaymentInfoWithSavedCard() {
      const paymentInfo = {
        card_id: this.card_id,
        cvv: this.cvv,
      };
      if (this.nonce) paymentInfo['three_ds'] = this.nonce;
      return paymentInfo;
    }
  }

  export class Traveller {
    traveller_id: string;
    first_name: string;
    last_name: string;
    dob: string;
    loyalty_card?: string;

    constructor(guest: User.Guest, loyaltyProgramNumber?: string) {
      // this.traveller_id = guest.traveller.traveller_id;
      // this.first_name = guest.traveller.first_name;
      // this.last_name = guest.traveller.last_name;
      // this.dob = guest.traveller.dob;
      // this.loyalty_card = loyaltyProgramNumber;
    }
  }

  export class ContactInfo {
    phone: string;
    email: string;
    phone_prefix: string;
    constructor(contacts: User.Contacts) {
      const _phone = contacts.phone.startsWith('+')
        ? contacts.phone
        : `+${contacts.phone}`;
      const ph = parsePhoneNumber(_phone);
      const phone = ph?.number?.significant;
      const code = ph.countryCode;
      this.email = contacts.email;
      this.phone = phone;
      this.phone_prefix = `+${code}`;
    }
  }

  export interface Price {
    token?: string;
    savings: number;
    use_credits?: boolean;
  }

  export class Payload {}

  export class HotelPayload extends Payload {
    token: string;
    special_request_comment: string;
    late_checkin_time: string;
    redirect_url: string;
    use_credits_on_book?: boolean;

    constructor(price: Price, specialRequest: string, arrivalTime: string) {
      super();
      this.token = price.token;
      this.special_request_comment = specialRequest ? specialRequest : '';
      this.late_checkin_time = arrivalTime ? arrivalTime : '';
      this.redirect_url = window.location.origin;
      this.use_credits_on_book = price.use_credits;
    }
  }

  export class CarPayload extends Payload {
    offer_key: string;
    test: boolean;

    constructor(offerkey: string) {
      super();
      this.offer_key = offerkey;
      this.test = true;
    }
  }

  export class FailureDialog {
    show: boolean = false;
    message: string = '';
    error: string = '';
    constructor() {}
  }

  export class ConfirmDialog {
    cvv: string = '';
    show: boolean = false;
    error: string = '';
  }

  export class LoadingDialog {
    show: boolean = false;
    message: string = '';
    title: string = '';
    constructor() {}
  }

  export class PendingOrder {
    order: OrderItem = null;
    show: boolean = false;
    source: PendingOrderSource = null;
    cancellation: DeferredOrderCancellationState = {
      show: false,
      loading: false,
      error: null,
    };

    constructor() {}

    static storeOrderId = (orderId: string) => {
      localStorage.setItem('pending_order_id', orderId);
    };

    static restore = () => {
      return localStorage.getItem('pending_order_id');
    };
  }

  export enum PendingOrderSource {
    Booking,
    Order,
    DeferredOrder,
    User,
    UpcomingTrips,
  }

  export interface OrderItem {
    user_id: string;
    order_id: string;
    order_number: string;
    booked_at: string;
    changed_timestamp: string;
    order_status: OrderItemStatus;
    product: OrderProduct;
    order_data: OrderData;
    cashback_notice: string;
    currency_conversion_notice: string;
    customer_service_channels: { webchat: boolean };
    order_currency: string;
    currency: string;
    receiptLink: string;
    refundable: boolean;
    static_host: string;
    price_details: OrderPriceDetails;
    payment_method: OrderPaymentMethod;
    payment_schedule: PaymentScheduleItem[];
    cancellation_info: string;
    cancellation_policies: HotelOffers.HotelRoomPolicyFine[];
    deposit_info: string;
    deposit_policies: HotelOffers.HotelRoomPolicyFine[];
    deposit_required: boolean;
    merchant_of_record: MerchantOfRecord;
    phone?: string;
    email?: string;
    policy?: HotelOffers.Policies;
    thrred_url?: string;
  }

  export enum OrderItemStatus {
    Booked = 'booked',
    Canceled = 'canceled',
    NoShow = 'no_show',
    Issued = 'issued',
    Pending = 'pending',
    Error = 'error',
    Created = 'created',
    UnknownStatus = 'unknown_status',
  }

  export enum OrderProduct {
    hotel = 'hotel',
    car = 'car',
    avia = 'avia',
  }

  export enum OrderSourceType {
    affiliate = 'affiliate',
    api = 'api',
  }

  export interface OrderData {
    supplier: string;
    supplier_name: string;
    supplier_color?: string;
    client_first_name: string;
    destination: string;
    reservation_status: ReservationStatus;
    agent_name: string;
    confirmation_number: string;
    is_confirmed_or_pending: boolean;
    is_confirmed: boolean;
    important_information: string;
    'covid-19': string;
    property: OrderDataProperty;
    booking_details: BookingDetails;
    property_info_by_supplier: PropertyInfoBySupplier;
    manage_order_direct: string | null;
  }

  export interface OrderDataProperty {
    name: string;
    rating: number;
    address: string;
    postal_code: string;
    city: string;
    state: string;
    country: string;
    location: Location;
    phone: string;
    images: OrderImage[];
    property_currency: string;
    stars: number;
  }

  export enum ReservationStatus {
    confirmed = 'confirmed',
    processing = 'processing',
    canceled = 'canceled',
    error = 'error',
    waitPayment = 'waitingPayment',
  }

  export interface Location {
    latitude: number;
    longitude: number;
  }

  export interface OrderImage {
    image_url: string;
    base64: string;
  }

  export interface BookingDetails {
    guest: BookingGuest;
    number_of_guests: string;
    checkin: string;
    checkout: string;
    checkin_time_from: string;
    checkout_time_until: string;
    nights: number;
    room: BookingRoom;
    special_requests: BookingSpecialRequests;
  }

  export interface BookingGuest {
    dob: string;
    type: string;
    gender: string;
    last_name: string;
    first_name: string;
    middle_name: string;
  }

  export interface BookingRoom {
    type: string;
    size_m2: number;
    size_ft: number;
    name: string;
    beds: string;
    view: string;
    free_wifi: boolean;
    internet_description: string;
    breakfast: boolean;
    mealplan_description: string;
    facilities: string[];
    description: string;
    images: OrderImage[];
  }

  export interface PropertyInfoBySupplier {
    name: string;
    id: string;
    rating: number;
    address: string;
    postal_code: string;
    city: string;
    state: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  }

  export interface OrderPriceDetails {
    cashback: number;
    nights: number;
    finalprice: number;
    price: number;
    public_price: number;
    room_rate: number;
    taxes_and_fees: number;
    additional_charges: OrderPriceExtraCharge[];
  }

  export interface OrderPriceExtraCharge {
    excluded?: boolean;
    price: number;
    name: string;
    charge_price_mode?: ChargePriceMode;
    type?: string;
    charge_amount?: number;
  }

  export enum ChargePriceMode {
    per_stay = 'per_stay',
    per_person_per_stay = 'per_person_per_stay',
    per_night = 'per_night',
    per_person_per_night = 'per_person_per_night',
    percentage = 'percentage',
    incalculable = 'incalculable',
    variable_amount = 'variable_amount',
  }

  export interface OrderPaymentMethod {
    type: string;
    cc_ending: string;
  }

  export interface BookingSpecialRequests {
    arrival_time: string;
    free_text: string;
  }

  export interface PaymentScheduleItem {
    billable_currency: string;
    billable_price: number;
    from: string;
    price: number;
    tag: string;
  }

  export enum MerchantOfRecord {
    property = 'property',
    finalprice = 'finalprice',
    eps = 'eps',
    bkn = 'bkn',
  }

  export interface OrderCancellationResponse {
    result: {
      status: ReservationStatus;
    };
  }
}
